import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { openOAuthPopup } from '../utils/composioAuth';
import { useOrganization } from '../context/OrganizationContext';
import { toast } from 'sonner';
import useActivation from '../hooks/useActivation';
import ActivationPanel from '../components/ActivationPanel';
import {
  Check, CircleNotch, Plugs, ArrowSquareOut, X,
  MagnifyingGlass, LinkBreak, Star, Key, CaretDown, CaretUp
} from '@phosphor-icons/react';

/* ═══════════════════════════════════════════════════════
   API KEY CONNECT MODAL
   ═══════════════════════════════════════════════════════ */

const ApiKeyModal = ({ app, onConnect, onClose, connecting }) => {
  const [apiKey, setApiKey] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="api-key-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src={app.logo} alt={app.name} className="w-8 h-8 rounded-lg" onError={e => { e.target.style.display = 'none'; }} />
            <h3 className="text-base font-bold text-[#F0F4F8]">Connect {app.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#7D8896] hover:text-[#F0F4F8] transition-colors"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#7D8896] mb-4">Enter your {app.name} API key to connect. You can find this in your {app.name} account settings.</p>
        <input
          data-testid="api-key-input"
          type="password"
          value={apiKey}
          onChange={e => setApiKey(e.target.value)}
          placeholder={`Paste your ${app.name} API key`}
          className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0]/40 mb-4"
        />
        <div className="flex gap-3">
          <button
            data-testid="api-key-submit"
            onClick={() => onConnect(app.slug, apiKey)}
            disabled={!apiKey.trim() || connecting}
            className="flex-1 px-4 py-2.5 bg-[#00E5C0] text-[#12161C] rounded-lg text-sm font-bold hover:bg-[#00D4B0] disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          >
            {connecting ? <CircleNotch size={14} className="animate-spin" /> : <Key size={14} weight="bold" />}
            Connect
          </button>
          <button onClick={onClose} className="px-4 py-2.5 border border-[rgba(255,255,255,0.1)] text-[#B0B8C4] rounded-lg text-sm hover:bg-[rgba(255,255,255,0.04)] transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   APP CARD — Compact single-line design
   ═══════════════════════════════════════════════════════ */

const AppCard = ({ app, onConnect, onDisconnect, connecting }) => {
  const isConnecting = connecting === app.slug;

  return (
    <div
      data-testid={`app-card-${app.slug}`}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)] transition-all duration-200 hover:-translate-y-[3px] hover:shadow-[0_12px_28px_rgba(0,0,0,0.45)] ${
        app.connected
          ? 'bg-[#1E2836] border-[rgba(0,229,192,0.15)]'
          : 'bg-[#1E2836] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.1)]'
      }`}
    >
      <div className="w-10 h-10 rounded-[10px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] flex items-center justify-center shrink-0">
        <img
          src={app.logo}
          alt={app.name}
          className="w-7 h-7 rounded-md object-contain"
          data-testid={`app-logo-${app.slug}`}
          onError={e => {
            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(app.name)}&background=1C2230&color=E6EDF3&size=32&font-size=0.4`;
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#F0F4F8] truncate">{app.name}</p>
      </div>
      {app.auth_type === 'api_key' && !app.connected && (
        <span className="text-[10px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 rounded flex-shrink-0">API key</span>
      )}
      {app.connected ? (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="flex items-center gap-1 px-2 py-1 bg-[rgba(0,229,192,0.08)] text-[#00E5C0] rounded-lg text-[11px] font-medium border border-[rgba(0,229,192,0.15)]" data-testid={`app-connected-${app.slug}`}>
            <Check size={10} weight="bold" />Connected
          </span>
          <button
            data-testid={`app-disconnect-${app.slug}`}
            onClick={() => onDisconnect(app.slug)}
            className="p-1 text-[#7D8896] hover:text-[#FF6B6B] rounded hover:bg-[rgba(255,107,107,0.08)] transition-all"
            title="Disconnect"
          ><LinkBreak size={12} /></button>
        </div>
      ) : (
        <button
          data-testid={`app-connect-${app.slug}`}
          onClick={() => onConnect(app)}
          disabled={isConnecting}
          className="px-3 py-1.5 bg-[#161B26] text-[#F0F4F8] rounded-lg text-xs font-medium border border-[rgba(255,255,255,0.1)] hover:bg-[#252D3D] hover:border-[rgba(255,255,255,0.2)] disabled:opacity-40 transition-all flex items-center gap-1.5 flex-shrink-0"
        >
          {isConnecting ? <CircleNotch size={12} className="animate-spin" /> : <ArrowSquareOut size={12} />}
          Connect
        </button>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   CATEGORY GROUP (collapsible)
   ═══════════════════════════════════════════════════════ */

const CategoryGroup = ({ label, apps, onConnect, onDisconnect, connecting, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  // Auto-open when search/filter changes
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  if (apps.length === 0) return null;

  const connectedCount = apps.filter(a => a.connected).length;

  return (
    <div className="mb-4" data-testid={`category-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-1 py-2 group"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em]">{label}</h3>
          <span className="text-[10px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 rounded">
            {connectedCount > 0 && <span className="text-[#00E5C0]">{connectedCount}/</span>}{apps.length}
          </span>
        </div>
        {open ? <CaretUp size={12} className="text-[#7D8896]" /> : <CaretDown size={12} className="text-[#7D8896]" />}
      </button>
      {open && (
        <div className="space-y-1.5 mt-1">
          {apps.map(app => (
            <AppCard key={app.slug} app={app} onConnect={onConnect} onDisconnect={onDisconnect} connecting={connecting} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════ */

const AppsPage = () => {
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.organization_id;
  const [searchParams] = useSearchParams();

  const [allApps, setAllApps] = useState([]);
  const [popular, setPopular] = useState([]);
  const [categories, setCategories] = useState({});
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [totalConnected, setTotalConnected] = useState(0);
  const [apiKeyApp, setApiKeyApp] = useState(null);
  const [showConnected, setShowConnected] = useState(false);
  const { triggerActivation, activationData, dismiss: dismissActivation } = useActivation();

  const fetchApps = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (activeCategory) params.set('category', activeCategory);
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/integrations/apps?${params}`,
        {},
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        setAllApps(data.apps || []);
        setPopular(data.popular || []);
        setCategories(data.categories || {});
        setCategoryOrder(data.category_order || []);
        setTotalConnected(data.total_connected || 0);
      }
    } catch (err) {
      console.error('Error loading apps:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, search, activeCategory]);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  // Handle OAuth callback — fire activation hook
  useEffect(() => {
    const connected = searchParams.get('connected');
    if (connected && connected !== 'false') {
      toast.success(`${connected} connected successfully`);
      setTimeout(fetchApps, 1500);
      triggerActivation(connected);
    }
  }, [searchParams, fetchApps, triggerActivation]);

  // Handle highlight param from setup flow CTAs
  const highlightApp = searchParams.get('highlight');
  useEffect(() => {
    if (highlightApp && !loading) {
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="app-card-${highlightApp}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-amber-400/50');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400/50'), 3000);
        }
      }, 300);
    }
  }, [highlightApp, loading]);

  const handleConnect = async (app) => {
    if (app.auth_type === 'api_key' && !app.connected) {
      setApiKeyApp(app);
      return;
    }
    setConnecting(app.slug);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/integrations/connect/${app.slug}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ callback_url: `${window.location.origin}/oauth/callback?connected=${app.slug}` }),
        },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        if (data.redirect_url) {
          toast.info(`Opening authorization...`);
          await openOAuthPopup(data.redirect_url);
          // Refresh connection list after popup closes
          await new Promise(r => setTimeout(r, 2000));
          fetchApps();
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not connect');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setConnecting(null);
    }
  };

  const handleApiKeyConnect = async (slug, apiKey) => {
    setConnecting(slug);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/integrations/connect/${slug}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ api_key: apiKey }),
        },
        orgId
      );
      if (res.ok) {
        toast.success(`${apiKeyApp?.name || slug} connected`);
        setApiKeyApp(null);
        fetchApps();
        triggerActivation(slug);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Connection failed');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (slug) => {
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/integrations/disconnect/${slug}`,
        { method: 'POST' },
        orgId
      );
      if (res.ok) {
        toast.success(`Disconnected`);
        fetchApps();
      }
    } catch {
      toast.error('Could not disconnect');
    }
  };

  // Group non-popular apps by category
  const grouped = useMemo(() => {
    const g = {};
    const nonPopular = search ? allApps : allApps.filter(a => !a.is_popular);
    nonPopular.forEach(app => {
      const cat = app.category || 'other';
      if (!g[cat]) g[cat] = [];
      g[cat].push(app);
    });
    return g;
  }, [allApps, search]);

  // Category pills for filter
  const activeCategoryOptions = useMemo(() => {
    const counts = {};
    allApps.forEach(app => {
      const cat = app.category || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return categoryOrder.filter(k => counts[k] > 0).map(k => ({
      key: k,
      label: categories[k] || k,
      count: counts[k],
    }));
  }, [allApps, categoryOrder, categories]);

  return (
    <div className="min-h-screen p-6 lg:p-8" data-testid="apps-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#F0F4F8] mb-1" data-testid="apps-heading">Connected Tools</h1>
            <p className="text-sm text-[#7D8896]">
              Connect your apps to let workers take action — <span className="text-[#00E5C0] font-medium">3,000+ tools available via Composio</span>
            </p>
          </div>
          {totalConnected > 0 && (
            <button
              data-testid="show-connected-btn"
              onClick={() => setShowConnected(!showConnected)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-semibold border transition-all ${
                showConnected
                  ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]'
                  : 'bg-[#1E2836] text-[#F0F4F8] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
              }`}
            >
              <Check size={12} weight="bold" className="text-[#00E5C0]" />
              {totalConnected} Connected
            </button>
          )}
        </div>

        {/* Connected Apps Panel */}
        {showConnected && (
          <div className="mb-6 bg-[#1E2836] border border-[rgba(0,229,192,0.15)] rounded-xl p-4" data-testid="connected-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#F0F4F8]">Connected Apps</h3>
              <button onClick={() => setShowConnected(false)} className="text-[#7D8896] hover:text-[#F0F4F8] transition-colors"><X size={14} /></button>
            </div>
            <div className="space-y-1.5">
              {allApps.filter(a => a.connected).map(app => (
                <div key={app.slug} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]">
                  <img src={app.logo} alt={app.name} className="w-7 h-7 rounded-lg object-contain" onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(app.name)}&background=1C2230&color=E6EDF3&size=28&font-size=0.4`; }} />
                  <span className="flex-1 text-sm text-[#F0F4F8]">{app.name}</span>
                  <button
                    data-testid={`panel-disconnect-${app.slug}`}
                    onClick={() => handleDisconnect(app.slug)}
                    className="px-2.5 py-1 text-[11px] text-[#FF6B6B] border border-[rgba(255,107,107,0.2)] rounded-lg hover:bg-[rgba(255,107,107,0.08)] transition-all"
                  >Disconnect</button>
                </div>
              ))}
              {allApps.filter(a => a.connected).length === 0 && (
                <p className="text-xs text-[#7D8896] py-2">No apps connected yet</p>
              )}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8896]" />
          <input
            data-testid="apps-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search apps..."
            className="w-full pl-8 pr-3 py-2.5 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#7D8896] focus:outline-none focus:border-[#00E5C0]/40 transition-colors"
          />
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            data-testid="filter-all"
            onClick={() => setActiveCategory('')}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
              !activeCategory
                ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]'
                : 'text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
            }`}
          >All</button>
          {activeCategoryOptions.map(cat => (
            <button
              key={cat.key}
              data-testid={`filter-${cat.key}`}
              onClick={() => setActiveCategory(activeCategory === cat.key ? '' : cat.key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                activeCategory === cat.key
                  ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border-[rgba(0,229,192,0.3)]'
                  : 'text-[#B0B8C4] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
              }`}
            >{cat.label} <span className="opacity-50">{cat.count}</span></button>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20" data-testid="apps-loading">
            <CircleNotch size={28} className="animate-spin text-[#00E5C0]" />
          </div>
        )}

        {/* Popular Section */}
        {!loading && !search && !activeCategory && popular.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 px-1 mb-3">
              <Star size={12} weight="fill" className="text-amber-400" />
              <h3 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em]">Popular</h3>
              <span className="text-[10px] text-[#7D8896] bg-[rgba(255,255,255,0.04)] px-1.5 py-0.5 rounded">{popular.length}</span>
            </div>
            <div className="space-y-1.5">
              {popular.map(app => (
                <AppCard key={app.slug} app={app} onConnect={handleConnect} onDisconnect={handleDisconnect} connecting={connecting} />
              ))}
            </div>
          </div>
        )}

        {/* Category Groups */}
        {!loading && categoryOrder.map(catKey => {
          const catApps = grouped[catKey] || [];
          const label = categories[catKey] || catKey;
          return (
            <CategoryGroup
              key={catKey}
              label={label}
              apps={catApps}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              connecting={connecting}
              defaultOpen={!!search || !!activeCategory}
            />
          );
        })}

        {/* Empty search */}
        {!loading && allApps.length === 0 && search && (
          <div className="text-center py-12" data-testid="apps-empty">
            <p className="text-[#7D8896] text-sm">No apps match "{search}"</p>
          </div>
        )}

        {/* API Key Modal */}
        {apiKeyApp && (
          <ApiKeyModal
            app={apiKeyApp}
            onConnect={handleApiKeyConnect}
            onClose={() => setApiKeyApp(null)}
            connecting={!!connecting}
          />
        )}

        <ActivationPanel activationData={activationData} onDismiss={dismissActivation} />
      </div>
    </div>
  );
};

export default AppsPage;
